import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Input } from './input';

/**
 * Autocomplete - Componente de autocomplete com busca
 * 
 * @param {Array} options - Array de opções: [{ id, code, description, ... }]
 * @param {Function} getOptionLabel - Função para obter o label: (option) => string
 * @param {Function} getOptionValue - Função para obter o valor: (option) => any
 * @param {any} value - Valor selecionado
 * @param {Function} onChange - Callback ao mudar: (value, option) => void
 * @param {string} placeholder - Placeholder do input
 * @param {boolean} required - Se é obrigatório
 * @param {boolean} disabled - Se está desabilitado
 * @param {string} className - Classes CSS adicionais
 * @param {Function} filterOptions - Função customizada para filtrar: (options, search) => filteredOptions
 * @param {string} dropdownClassName - Classes extras no painel da lista (ex.: z-index)
 */
const Autocomplete = React.forwardRef(({
    options = [],
    getOptionLabel = (option) => `${option.code || ''} - ${option.description || ''}`,
    getOptionValue = (option) => option.id,
    value,
    onChange,
    placeholder = 'Digite para buscar...',
    required = false,
    disabled = false,
    className,
    filterOptions,
    dropdownClassName,
    'aria-label': ariaLabel,
}, ref) => {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [selectedOption, setSelectedOption] = useState(null);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Encontra a opção selecionada
    useEffect(() => {
        if (value) {
            const option = options.find(opt => getOptionValue(opt) === value);
            setSelectedOption(option);
            if (option) {
                setSearch(getOptionLabel(option));
            } else {
                setSearch('');
            }
        } else {
            setSelectedOption(null);
            setSearch('');
        }
    }, [value, options, getOptionLabel, getOptionValue]);

    // Filtra opções quando o search muda
    useEffect(() => {
        if (!search || search.trim() === '') {
            setFilteredOptions(options);
            return;
        }

        const searchLower = search.toLowerCase();
        if (filterOptions) {
            setFilteredOptions(filterOptions(options, search));
        } else {
            const filtered = options.filter(option => {
                const label = getOptionLabel(option).toLowerCase();
                return label.includes(searchLower);
            });
            setFilteredOptions(filtered);
        }
    }, [search, options, getOptionLabel, filterOptions]);

    // Fecha dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const newSearch = e.target.value;
        setSearch(newSearch);
        setIsOpen(true);
        
        // Se limpar o input, limpa a seleção
        if (!newSearch) {
            setSelectedOption(null);
            onChange(null, null);
            // Remove required validation
            if (inputRef.current) {
                inputRef.current.setCustomValidity('');
            }
        }
    };

    const handleSelectOption = (option) => {
        const optionValue = getOptionValue(option);
        const optionLabel = getOptionLabel(option);
        
        setSelectedOption(option);
        setSearch(optionLabel);
        setIsOpen(false);
        onChange(optionValue, option);
        
        // Remove required validation quando seleciona
        if (inputRef.current) {
            inputRef.current.setCustomValidity('');
        }
    };

    const handleInputFocus = () => {
        setIsOpen(true);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Escape') {
            // Lista aberta: ESC fecha só a lista, sem fechar o drawer que a contém
            if (isOpen) e.preventDefault();
            setIsOpen(false);
        } else if (e.key === 'Enter' && filteredOptions.length === 1) {
            e.preventDefault();
            handleSelectOption(filteredOptions[0]);
        }
    };

    return (
        <div ref={containerRef} className={cn('relative', className)}>
            <Input
                ref={(node) => {
                    inputRef.current = node;
                    if (typeof ref === 'function') {
                        ref(node);
                    } else if (ref) {
                        ref.current = node;
                    }
                }}
                type="text"
                value={search}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onKeyDown={handleInputKeyDown}
                onInvalid={(e) => {
                    if (required && !selectedOption) {
                        e.target.setCustomValidity('Por favor, selecione uma opção');
                    }
                }}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                autoComplete="off"
                aria-label={ariaLabel}
            />
            
            {/* Dropdown */}
            {isOpen && filteredOptions.length > 0 && (
                <div
                    className={cn(
                        'absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto',
                        dropdownClassName,
                    )}
                >
                    {filteredOptions.map((option, index) => {
                        const optionValue = getOptionValue(option);
                        const optionLabel = getOptionLabel(option);
                        const isSelected = selectedOption && getOptionValue(selectedOption) === optionValue;
                        
                        return (
                            <button
                                key={optionValue || index}
                                type="button"
                                onClick={() => handleSelectOption(option)}
                                className={cn(
                                    'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors',
                                    isSelected && 'bg-primary-50 text-primary-600'
                                )}
                            >
                                {optionLabel}
                            </button>
                        );
                    })}
                </div>
            )}
            
            {/* Mensagem quando não há resultados */}
            {isOpen && search && filteredOptions.length === 0 && (
                <div
                    className={cn(
                        'absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500',
                        dropdownClassName,
                    )}
                >
                    Nenhum resultado encontrado
                </div>
            )}
        </div>
    );
});

Autocomplete.displayName = 'Autocomplete';

export { Autocomplete };

